import React, { useState, useCallback, useEffect } from 'react';
import { Briefcase, Search, Filter, RefreshCw, Download, ExternalLink, AlertTriangle, Eye } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/Badge';
import { useTeams } from '../../contexts/TeamsContext';
import { BrandApi } from 'api-client';
import { getApiConfiguration } from '../../api/utils';
import { BrandInfringement } from 'api-client';
import { TablePagination } from '../../components/ui/TablePagination';
import { AuthContext } from '../../contexts/AuthContext';
import { useContext } from 'react';
import { Link } from 'react-router-dom';
import ExportActionBtn from '@/components/ui/ExportActionBtn';

interface PaginationInfo {
  count: number;
  next: string | null;
  previous: string | null;
}

const InfringementsList = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedType, setSelectedType] = useState('all');
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [infringements, setInfringements] = useState<BrandInfringement[]>([]);
  const [paginationInfo, setPaginationInfo] = useState<PaginationInfo>({
    count: 0,
    next: null,
    previous: null,
  });
  const { token, user } = useContext(AuthContext);

  const { currentTeam } = useTeams();
  const itemsPerPage = 10;

  const fetchInfringements = useCallback(async () => {
    if (!currentTeam?.slug) return;
    
    setLoading(true);
    try {
      const brandApi = new BrandApi(getApiConfiguration(token));
      const response = await brandApi.brandInfringementsList({
        teamSlug: currentTeam.slug
      });

      setInfringements(response.results || []);
      setTotalCount(response.count || 0);
      setPaginationInfo({
        count: response.count || 0,
        next: String(response.next),
        previous: String(response.previous),
      });
    } catch (error) {
      console.error('Error fetching infringements:', error);
    } finally {
      setLoading(false);
    }
  }, [currentTeam?.slug, currentPage, itemsPerPage, searchQuery, selectedType, selectedStatus]);

  const fetchAllInfringements = async (limit: number) => {
    if (!currentTeam?.slug || !token) return [];
    
    const brandApi = new BrandApi(getApiConfiguration(token));
    const response = await brandApi.brandInfringementsList({
      teamSlug: currentTeam.slug,
      limit
    });
    return response.results;
  };

  useEffect(() => {
    fetchInfringements();
  }, [fetchInfringements]);

  const handleRefresh = () => {
    fetchInfringements();
  };

  const getSeverityColor = (severity: string | undefined) => {
    switch (severity) {
      case 'critical':
        return 'critical';
      case 'high':
        return 'high';
      case 'medium':
        return 'medium';
      case 'low':
        return 'low';
      default:
        return 'default';
    }
  };

  const getStatusColor = (status: string | undefined) => {
    switch (status) {
      case 'new':
        return 'default';
      case 'detected':
        return 'info';
      case 'investigating':
        return 'warning';
      case 'reported':
        return 'warning';
      case 'takendown':
        return 'success';
      default:
        return 'default';
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <Briefcase className="h-8 w-8 text-blue-500" />
          <div>
            <h1 className="text-2xl font-bold text-white">Brand Infringements</h1>
            <p className="text-sm text-gray-400">Track and manage brand protection violations</p>
          </div>
        </div>
        <div className="flex items-center space-x-3">
          <ExportActionBtn
            fetchAllData={() => fetchAllInfringements(paginationInfo.count)}
            filename="infringements"
            disabled={infringements?.length === 0}
          />
          <Button onClick={handleRefresh} disabled={loading}>
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex items-center space-x-4">
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500 h-4 w-4" />
          <input
            type="text"
            placeholder="Search infringements..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-2 bg-[#1A1F2E] border border-gray-800 rounded-lg text-gray-300 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500/50"
          />
        </div>
        <Button variant="outline">
          <Filter className="h-4 w-4 mr-2" />
          Filter
        </Button>
      </div>

      {/* Infringements Table */}
      <div className="bg-[#0F1629] rounded-xl border border-gray-800/50 overflow-hidden">
        <table className="w-full">
          <thead>
            <tr className="border-b border-gray-800">
              <th className="px-6 py-4 text-left text-sm font-medium text-gray-400">Title</th>
              <th className="px-6 py-4 text-left text-sm font-medium text-gray-400">Type</th>
              <th className="px-6 py-4 text-left text-sm font-medium text-gray-400">Source</th>
              <th className="px-6 py-4 text-left text-sm font-medium text-gray-400">Severity</th>
              <th className="px-6 py-4 text-left text-sm font-medium text-gray-400">Status</th>
              <th className="px-6 py-4 text-left text-sm font-medium text-gray-400">Confidence</th>
              <th className="px-6 py-4 text-left text-sm font-medium text-gray-400">Detected</th>
              <th className="px-6 py-4 text-left text-sm font-medium text-gray-400">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-800">
            {infringements.map((infringement) => (
              <tr key={infringement.id} className="group hover:bg-gray-800/30">
                <td className="px-6 py-4">
                  <span className="text-sm text-white">{infringement.threatTitle}</span>
                </td>
                <td className="px-6 py-4">
                  <span className="text-sm text-gray-300">{infringement.threatTypeDisplay}</span>
                </td>
                <td className="px-6 py-4">
                  <span className="text-sm text-gray-300">{infringement.source}</span>
                </td>
                <td className="px-6 py-4">
                  <Badge variant={getSeverityColor(infringement.severity)}>
                    {infringement.severityDisplay}
                  </Badge>
                </td>
                <td className="px-6 py-4">
                  <Badge variant={getStatusColor(infringement.status)}>
                    {infringement.statusDisplay}
                  </Badge>
                </td>
                <td className="px-6 py-4">
                  <span className="text-sm text-gray-300">
                    {infringement.confidenceLevel !== null ? `${infringement.confidenceLevel}%` : 'Unknown'}
                  </span>
                </td>
                <td className="px-6 py-4">
                  <span className="text-sm text-gray-300">
                    {new Date(infringement.dateDetected).toLocaleString()}
                  </span>
                </td>
                <td className="px-6 py-4">
                  <Link
                    to={`/brand-protection/infringements/${infringement.id}`}
                    className="p-2 rounded-lg text-gray-400 hover:text-blue-500 hover:bg-gray-800/50 transition-colors inline-flex items-center"
                    title="View Details"
                  >
                    <Eye className="h-4 w-4" />
                  </Link>
                </td>
              </tr>
            ))}
            {infringements.length === 0 && !loading && (
              <tr>
                <td colSpan={8} className="px-6 py-8 text-center text-gray-500">
                  No infringements found
                </td>
              </tr>
            )}
            {loading && (
              <tr>
                <td colSpan={8} className="px-6 py-8 text-center text-gray-500">
                  Loading...
                </td>
              </tr>
            )}
          </tbody>
        </table>

        {/* Pagination */}
        <div className="px-6 py-4 border-t border-gray-800">
          <TablePagination
            currentPage={currentPage}
            onPageChange={setCurrentPage}
            totalItems={totalCount}
            itemsPerPage={itemsPerPage}
            paginationInfo={paginationInfo}
            itemName="infringements"
          />
        </div>
      </div>
    </div>
  );
};

export default InfringementsList;